import { requestService } from '@services/request';
import { Audience, Campaign, Reminder } from '@vincerosoft/assessfluence-types';

type ApiPagination = {
  next?: string | null;
  previous?: string | null;
  limit?: number;
  count?: number;
  has_next?: boolean;
};

const baseUrl = '/v1/campaigns';

function campaignsApiFactory() {
  /**
   * Get campaigns list with pagination
   */
  function getCampaigns(params?: {
    limit?: number;
    cursor?: string;
    entity_id?: string;
    status?: string;
    owned_by?: string;
  }) {
    return requestService<{
      success: boolean;
      data: Campaign[];
      message: string;
      pagination: ApiPagination;
    }>(baseUrl, {
      query: params
    });
  }

  /**
   * Get campaign invitations
   */
  function getCampaignInvitations(
    campaignId: string,
    params?: {
      limit?: number;
      cursor?: string;
    }
  ) {
    return requestService<{
      success: boolean;
      data: unknown[];
      message: string;
      pagination: ApiPagination;
    }>(`${baseUrl}/${campaignId}/invitations`, {
      query: params
    });
  }

  /**
   * Get campaign by ID
   */
  function getCampaign(campaignId: string) {
    return requestService<{
      success: boolean;
      data: Campaign;
      message: string;
    }>(`${baseUrl}/${campaignId}`);
  }

  /**
   * Delete campaign
   */
  function deleteCampaign(campaignId: string) {
    return requestService<{
      success: boolean;
      data: Campaign;
      message: string;
    }>(`${baseUrl}/${campaignId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Launch campaign
   */
  function launchCampaign(campaignId: string) {
    return requestService<{
      success: boolean;
      data: Campaign;
      message: string;
      meta: {
        invitations_count?: number | undefined;
        license_usage?:
          | {
              grant_id: string;
              new_usage: number;
              previous_usage: number;
              delta: number;
            }
          | undefined;
      };
    }>(`${baseUrl}/${campaignId}/launch`, {
      method: 'POST',
      body: {}
    });
  }

  /**
   * Reopen campaign
   */
  function reopenCampaign(campaignId: string, date?: Date) {
    const closingDate = date?.toISOString();
    return requestService<{
      success: boolean;
      data: Campaign;
      message: string;
    }>(`${baseUrl}/${campaignId}/reopen`, {
      method: 'PATCH',
      body: {},
      query: {
        closing_date: closingDate
      }
    });
  }

  /**
   * Close campaign
   */
  function closeCampaign(campaignId: string) {
    return requestService<{
      success: boolean;
      data: Campaign;
      message: string;
      meta: {
        task_id: string;
        status: string;
        created_at: string;
        updated_at: string;
      };
    }>(`${baseUrl}/${campaignId}/close`, {
      method: 'PATCH',
      body: {}
    });
  }

  /**
   * Create campaign reminder
   */
  function createCampaignReminder(campaignId: string) {
    return requestService<{
      success: boolean;
      data: Reminder;
      message: string;
    }>(`${baseUrl}/${campaignId}/reminders`, {
      method: 'POST',
      body: {}
    });
  }

  /**
   * Delete campaign reminder
   */
  function deleteCampaignReminder(campaignId: string) {
    return requestService<{
      success: boolean;
      data: Reminder;
      message: string;
    }>(`${baseUrl}/${campaignId}/reminders`, {
      method: 'DELETE'
    });
  }

  /**
   * Send campaign reminder
   */
  function sendCampaignReminder(
    campaignId: string,
    reminderId: string,
    type: 'manual' | 'scheduled'
  ) {
    return requestService<{
      success: boolean;
      data: Reminder;
      message: string;
    }>(`${baseUrl}/${campaignId}/reminders/${reminderId}/send`, {
      query: {
        type
      }
    });
  }

  /**
   * Update campaign audience
   */
  function updateCampaignAudience(
    campaignId: string,
    formId: string,
    data: {
      member_ids?: string[] | string;
      list_ids?: string[];
    }
  ) {
    return requestService<{
      success: boolean;
      data: Audience;
      message: string;
      meta: {
        task_id: string;
        status: string;
        created_at: string;
        updated_at: string;
      };
    }>(`${baseUrl}/${campaignId}/audience/${formId}`, {
      method: 'PATCH',
      body: data
    });
  }

  return {
    getCampaigns,
    getCampaignInvitations,
    getCampaign,
    deleteCampaign,
    launchCampaign,
    reopenCampaign,
    closeCampaign,
    createCampaignReminder,
    deleteCampaignReminder,
    sendCampaignReminder,
    updateCampaignAudience
  };
}

export const campaignsApiService = campaignsApiFactory();
